import { GQL } from "@binale-tech/shared";
import { saveAs } from "file-saver";
import { getMetadata, ref as refStorage, uploadBytesResumable } from "firebase/storage";
import JSZip from "jszip";
import { getDocument } from "pdfjs-dist";
import { Dispatch, SetStateAction } from "react";
import { useGqlMutator } from "../../../scripts/graphql/useGqlMutator";
import { storage } from "../../../scripts/api/firebase/firebase";
import {
    documentCreate,
    documentsDelete,
    documentsTypeUpdate,
    documentsUpdate,
} from "../../../scripts/context/mutations/documentMutations.graphql";
import { logger } from "../../../scripts/infrastructure/logger";
import { DmsDefaultSubType, DmsType, TUploadFile, UploadFileStatus } from "@dms/types";
import { convertDocumentToUpdateInput } from "@dms/scripts/helpers/convertDocumentToUpdateInput";
import { ALL_DOCUMENTS } from "@dms/configs/constants";

export type TFileUploadEnrichedData = {
    fileData: Pick<TUploadFile, "file" | "hash" | "isUploaded">;
    documentType: { type: DmsType | typeof ALL_DOCUMENTS; subType?: string };
    companyData: GQL.ICompany;
    shouldSkipDmsDataProcessingOnUpload?: boolean;
};

type DocumentsApiCreateInput = {
    fileUploadData: TFileUploadEnrichedData;
    mutator: ReturnType<typeof useGqlMutator>;
    setFileStatus: (hash: string, data: Pick<TUploadFile, "status" | "error" | "isUploaded">) => void;
};

export class DocumentsApi {
    private static readonly maxFileSize = 20971520;
    private static readonly NS = "files";
    private static readonly fileFolderName = "pdf";
    private static readonly previewFolderName = "preview";

    static createDocument = async (arg: DocumentsApiCreateInput) => {
        const { fileUploadData, mutator, setFileStatus } = arg;
        const { fileData, documentType } = fileUploadData;

        await this.uploadFile({
            fileUploadData,
            mutator,
            setFileStatus,
        }).catch(err => {
            setFileStatus(fileData.hash, {
                error: err.message,
                status: UploadFileStatus.ERROR,
                isUploaded: false,
            });
            return Promise.reject(err);
        });

        const input: GQL.IDocumentCreateInput = {
            key: fileUploadData.fileData.hash,
            fileName: fileUploadData.fileData.file.name,
            companyId: fileUploadData.companyData.id,

            type: documentType.type,
            subType: documentType.subType,
        };

        await mutator.mutate({
            mutation: documentCreate,
            input,
            hideMessages: true,
        });

        setFileStatus(fileData.hash, {
            error: false,
            status: UploadFileStatus.DONE,
            isUploaded: true,
        });

        return { fileRes: { ...fileData, isUploaded: true } };
    };

    static uploadFile = async (arg: DocumentsApiCreateInput): Promise<void> => {
        const { fileUploadData, setFileStatus } = arg;
        return new Promise((resolve, reject) => {
            const { file, hash } = fileUploadData.fileData;
            const { companyData } = fileUploadData;

            if (file.size > this.maxFileSize) {
                setFileStatus(hash, {
                    error: `Size limit ${this.maxFileSize} bytes`,
                    status: UploadFileStatus.ERROR,
                    isUploaded: false,
                });

                reject(new Error(`Size limit ${this.maxFileSize} bytes`));
                return;
            }

            const name = hash + ".pdf";
            const { id: companyId } = companyData;

            const fileRef = refStorage(storage, [this.NS, companyId, this.fileFolderName, name].join("/"));
            const uploadTask = uploadBytesResumable(fileRef, file, {
                contentType: "application/pdf",
                customMetadata: {
                    documentId: hash,
                    companyId,
                    shouldSkipDmsDataProcessingOnUpload: fileUploadData.shouldSkipDmsDataProcessingOnUpload
                        ? "yes"
                        : undefined,
                },
            });

            setFileStatus(hash, {
                error: false,
                status: UploadFileStatus.PROGRESS,
                isUploaded: false,
            });

            uploadTask.on(
                "state_changed",
                () => {},
                (error: { message: string }) => {
                    setFileStatus(hash, {
                        error: error.message,
                        status: UploadFileStatus.ERROR,
                        isUploaded: false,
                    });

                    reject(error);
                },
                () => {
                    resolve();
                }
            );
        });
    };

    static updateDocuments = async (arg: {
        documents: GQL.IDocument[];
        mutator: ReturnType<typeof useGqlMutator>;
    }): Promise<{ documentsUpdate: true | undefined }> => {
        const { documents, mutator } = arg;
        const documentInputs = documents.map(convertDocumentToUpdateInput);

        return (await mutator.mutate({
            mutation: documentsUpdate,
            input: documentInputs,
            hideMessages: true,
        })) as { documentsUpdate: true | undefined };
    };

    static changeDocumentsType = async (documents: GQL.IDocument[], mutator: ReturnType<typeof useGqlMutator>) => {
        const checkSubtype = (subType: string) => {
            if (!subType) {
                return false;
            }

            const disallowedSubTypes: string[] = [DmsDefaultSubType.no_subTypes, DmsDefaultSubType.all_subTypes];

            const hasSubstring =
                subType.includes(DmsDefaultSubType.no_subTypes) || subType.includes(DmsDefaultSubType.all_subTypes);

            return !disallowedSubTypes.includes(subType) && !hasSubstring;
        };

        const documentTypeInputs = documents.map(doc => {
            const subType = checkSubtype(doc.subType) ? doc.subType : null;
            return {
                id: doc.key,
                type: doc.type,
                subType,
                companyId: doc.companyId,
            };
        });

        await mutator.mutate({
            mutation: documentsTypeUpdate,
            input: documentTypeInputs,
            hideMessages: true,
        });
    };

    static sendToTrash = async (
        documents: GQL.IDocument[],
        mutator: ReturnType<typeof useGqlMutator>
    ): Promise<void> => {
        if (documents.length === 0) {
            return;
        }

        const toTrashDocument = documents.map(document => {
            return {
                ...document,
                type: GQL.IDocumentStatus.Trash,
                subType: undefined,
            } as GQL.IDocument;
        });
        await this.changeDocumentsType(toTrashDocument, mutator);
    };

    static async removeDocuments(
        documents: GQL.IDocument[],
        mutator: ReturnType<typeof useGqlMutator>
    ): Promise<boolean> {
        await mutator.mutate<"documentsDelete", GQL.IDocumentDeleteInput[]>({
            mutation: documentsDelete,
            input: documents.map(doc => ({ key: doc.key, companyId: doc.companyId })),
            hideMessages: true,
        });
        return true;
    }

    static downloadFile = (url: string, fileName?: string) => {
        const storageRef = refStorage(storage, url);
        getMetadata(storageRef).then(value => {
            fetch(url)
                .then(response => response.blob())
                .then(blob => {
                    saveAs(blob, fileName || value?.customMetadata?.name);
                })
                .catch(error => {
                    console.log(error);
                });
        });
    };

    static bulkDownloadFiles = async (files: GQL.IDocument[], setIsDownload: Dispatch<SetStateAction<boolean>>) => {
        setIsDownload(true);
        const zip = new JSZip();
        const usedFileNames = new Set<string>();

        try {
            for (const [index, file] of files.entries()) {
                const name = usedFileNames.has(file.fileName) ? `copy-${index}_${file.fileName}` : file.fileName;
                const response = await fetch(file.fileUrl);
                const blob = await response.blob();
                zip.file(name, blob, { binary: true });
                usedFileNames.add(name);
            }
            const content = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
            saveAs(content, "dms-documents");
            setIsDownload(false);
        } catch (error) {
            logger.crit(error);
        }
    };

    static urlFormatter = (url: string = ""): string => {
        const indexQueryStart = url.indexOf("?");
        return url.slice(0, indexQueryStart);
    };

    static checkPDF = (file: File): Promise<boolean> => {
        return new Promise(resolve => {
            const reader = new FileReader();
            reader.onloadend = async () => {
                try {
                    const data = reader.result as ArrayBuffer;
                    const loadingTask = getDocument({
                        data,
                        isEvalSupported: false,
                        useSystemFonts: true,
                    });
                    const pdf = await loadingTask.promise;
                    const numPages = pdf.numPages;
                    await pdf.cleanup();
                    await pdf.destroy();
                    resolve(Boolean(numPages));
                } catch (error) {
                    resolve(false);
                }
            };
            reader.readAsArrayBuffer(file);
        });
    };
}
